import type {
    ClearArgs,
    DrawArgs,
    HalImage,
    HalImageRef,
    HalImageVirtual,
    HalImageVirtualStats,
    PageDescriptor,
    PartialHalImageDescriptor,
    ReadImageDataArgs,
    WriteImageDataArgs,
} from "@cm/hal"
import {
    completeHalImageDescriptor,
    getChannelLayoutFromImageSourceData,
    getDataTypeFromImageSourceData,
    getNumChannels,
    getSourceDataSize,
    isHalImagePhysical,
    isHalImageVirtual,
} from "@cm/hal"
import type {WebGl2Context} from "#webgl2/webgl2-context"
import {WebGl2ImageAtlas} from "#webgl2/webgl2-image-atlas"
import type {Box2Like, Size2Like, Vector2Like} from "@cm/math"
import {Box2, Matrix3x2, Vector2} from "@cm/math"
import type {WebGl2ImageDescriptor} from "#webgl2/webgl2-image"
import type {WebGl2ImageVirtualMipmapInfo} from "#webgl2/webgl2-image-virtual-mipmap-info"
import type {AllocatedUniquePageInfo, PageHandleInfo} from "#webgl2/webgl2-image-virtual-page-manager"
import {WebGl2ImageVirtualPageManager} from "#webgl2/webgl2-image-virtual-page-manager"
import {IsDefined, traceObject} from "@cm/utils"
import {WebGl2ImageBase} from "#webgl2/webgl2-image-base"
import {Subject} from "rxjs"
import type {WebGl2ImagePhysical} from "#webgl2/webgl2-image-physical"
import type {ViewTransformArgs} from "@cm/hal/hal-paintable"
import {loggerFactory} from "@cm/browser-log"

const USE_CLEAR_PAGE_SHARING_OPTIMIZATION = false // TODO support regional clearing
const USE_COPY_PAGE_SHARING_OPTIMIZATION = true

export class WebGl2ImageVirtual extends WebGl2ImageBase implements HalImageVirtual {
    readonly isHalImageVirtual = true

    readonly pageAdded = new Subject<PageDescriptor>()
    readonly pageRemoved = new Subject<PageDescriptor>()
    readonly beforePageRemoved = new Subject<PageDescriptor>()

    readonly forceAlphaToOne = false

    constructor(
        readonly context: WebGl2Context,
        partialDescriptor: PartialHalImageDescriptor,
    ) {
        super()
        const descriptor = completeHalImageDescriptor(partialDescriptor)
        this._pageManager = new WebGl2ImageVirtualPageManager(descriptor, context)
        this._pageManager.pageAdded.subscribe((pageDescriptor) => this.pageAdded.next(pageDescriptor))
        this._pageManager.pageRemoved.subscribe((pageDescriptor) => {
            this._maxRecentlyWrittenMipLevelPage = this.descriptor.numMipLevels - 1 // if we remove a page, we potentially need to propagate coarser mip levels to missing pages along the full chain
            this.pageRemoved.next(pageDescriptor)
        })
        this._pageManager.beforePageRemoved.subscribe((pageDescriptor) => this.beforePageRemoved.next(pageDescriptor))
    }

    // HalEntity
    dispose(): void {
        this._pageManager.dispose()
        this.freeAllPages()
    }

    setPagePriority(pagePriority: number): void {
        this._pageManager.setPagePriority(pagePriority)
    }

    // HalImage
    get descriptor(): WebGl2ImageDescriptor {
        return this._pageManager.webGl2ImageDescriptor
    }

    get mipmapInfo(): WebGl2ImageVirtualMipmapInfo {
        return this._pageManager.mipmapInfo
    }

    get stats(): HalImageVirtualStats {
        const allocatedPages: PageDescriptor[] = []
        const sharedPages: PageDescriptor[] = []
        const lockedPages: PageDescriptor[] = []
        for (const pageHandle of this._pageManager.getAllocatedPageHandles()) {
            pageHandle.associatedPageDescriptorsByPageHash.forEach((pageDescriptor) => {
                allocatedPages.push(pageDescriptor)
                if (pageHandle.sharedHandle.totalRefCount > 1) {
                    sharedPages.push(pageDescriptor)
                }
                if (pageHandle.sharedHandle.totalLockCount > 0) {
                    lockedPages.push(pageDescriptor)
                }
            })
        }
        return {
            ...this._pageManager.pageTableImage.stats,
            numAtlasPagesPerVirtualPage: this.descriptor.numLayers,
            allocatedPages,
            sharedPages,
            lockedPages,
        }
    }

    // HalImageVirtual
    get pageTableTexture(): WebGl2ImagePhysical {
        return this._pageManager.pageTableImage
    }

    // HalPaintable
    beginDraw(args: DrawArgs): number {
        if (this._currentDrawInfo) {
            throw new Error("Drawing already in progress.")
        }
        if (args.layer !== 0) {
            throw new Error("Multiple layers not supported for virtual image.")
        }

        // write the entire page (considering page-shared mip-levels)
        const mipLevelSize = this.getMipLevelSize(args.mipLevel)
        const pageSize = {
            width: Math.min(mipLevelSize.width, WebGl2ImageAtlas.pageSize),
            height: Math.min(mipLevelSize.height, WebGl2ImageAtlas.pageSize),
        }
        // const pageSize = {
        //     width: WebGl2ImageAtlas.pageSize,
        //     height: WebGl2ImageAtlas.pageSize,
        // }
        const p00 = {
            x: Math.floor(args.clipRegion.x / pageSize.width) * pageSize.width,
            y: Math.floor(args.clipRegion.y / pageSize.height) * pageSize.height,
        }
        const p11 = {
            x: Math.ceil((args.clipRegion.x + args.clipRegion.width) / pageSize.width) * pageSize.width,
            y: Math.ceil((args.clipRegion.y + args.clipRegion.height) / pageSize.height) * pageSize.height,
        }
        const pageExtendedClipRegion = {
            x: p00.x,
            y: p00.y,
            width: p11.x - p00.x,
            height: p11.y - p00.y,
        }

        this._currentDrawInfo = {
            pageExtendedClipRegion,
            bufferImage: this.context.createPhysicalImage({
                width: pageExtendedClipRegion.width,
                height: pageExtendedClipRegion.height,
                channelLayout: "RGBA", // fix this to maximum channel layout to facilitate reusing the buffer image
                dataType: "float32", // fix this to maximum data type to facilitate reusing the buffer image
            }),
            bufferDrawArgs: {
                clipRegion: {
                    x: 0,
                    y: 0,
                    width: pageExtendedClipRegion.width,
                    height: pageExtendedClipRegion.height,
                },
                transformOffset: {x: 0, y: 0},
                uvTransformOffset: {x: 0, y: 0},
                layer: 0,
                mipLevel: 0,
                isReplacing: args.isReplacing,
            },
        }
        if (!args.isReplacing) {
            // move atlas pages buffer
            this.context.blit({
                sourceImage: this,
                sourceRegion: pageExtendedClipRegion,
                sourceMipLevel: args.mipLevel,
                targetImage: this._currentDrawInfo.bufferImage.ref,
            })
        }
        return this._currentDrawInfo.bufferImage.ref.beginDraw(this._currentDrawInfo.bufferDrawArgs)
    }

    // HalPaintable
    beginDrawPass(args: DrawArgs, pass: number): ViewTransformArgs {
        if (!this._currentDrawInfo) {
            throw new Error("Drawing not started.")
        }
        const {viewTransform, uvOffset} = this._currentDrawInfo.bufferImage.ref.beginDrawPass(this._currentDrawInfo.bufferDrawArgs, pass)
        viewTransform.append(new Matrix3x2().translate(Vector2.fromVector2Like(this._currentDrawInfo.pageExtendedClipRegion).negate()))
        return {
            viewTransform,
            uvOffset,
        }
    }

    // HalPaintable
    endDrawPass(args: DrawArgs, pass: number): void {
        if (!this._currentDrawInfo) {
            throw new Error("Drawing not started.")
        }
        this._currentDrawInfo.bufferImage.ref.endDrawPass(this._currentDrawInfo.bufferDrawArgs, pass)
    }

    // HalPaintable
    endDraw(args: DrawArgs): void {
        if (!this._currentDrawInfo) {
            throw new Error("Drawing not started.")
        }
        this._currentDrawInfo.bufferImage.ref.endDraw(this._currentDrawInfo.bufferDrawArgs)
        this.copyToAtlas(this._currentDrawInfo.pageExtendedClipRegion, args.mipLevel, this._currentDrawInfo.bufferImage.ref, {x: 0, y: 0})
        this._currentDrawInfo.bufferImage.release()
        this._currentDrawInfo = null
    }

    // HalPaintable
    get width(): number {
        return this.descriptor.width
    }

    // HalPaintable
    get height(): number {
        return this.descriptor.height
    }

    // HalPaintable
    clear(args?: ClearArgs) {
        if (!USE_CLEAR_PAGE_SHARING_OPTIMIZATION) {
            this.context.fill({
                target: this,
                targetRegion: args?.region,
                targetMipLevel: args?.mipLevel,
                color: args?.color ?? {r: 0, g: 0, b: 0, a: 0},
            })
            return
        }

        // when clearing a virtual image, we clear only a single page and shared it among all pages
        if (
            args?.region &&
            (args.region.x !== 0 || args.region.y !== 0 || args.region.width < this.descriptor.width || args.region.height < this.descriptor.height)
        ) {
            throw new Error("Region not supported atm.")
        }
        let pageHandleInfo: PageHandleInfo | null = null
        let firstMipLevel, lastMipLevel: number
        if (args?.mipLevel !== undefined) {
            firstMipLevel = args.mipLevel
            lastMipLevel = args.mipLevel
        } else {
            firstMipLevel = 0
            lastMipLevel = this.descriptor.numMipLevels - 1
        }
        for (let mipLevel = firstMipLevel; mipLevel <= lastMipLevel; mipLevel++) {
            const mipLevelSize = this.getMipLevelSize(mipLevel)
            const pageDescriptors = this.determinePages({x: 0, y: 0, ...mipLevelSize}, mipLevel)
            if (pageDescriptors.length === 0) {
                throw new Error("No pages found.")
            }
            if (!pageHandleInfo) {
                const pageDescriptor = pageDescriptors[0]
                pageHandleInfo = this._pageManager.getOrAllocUniquePage(pageDescriptor).pageHandleInfo
                this.context.fill({
                    target: this,
                    targetRegion: pageDescriptor.region,
                    targetMipLevel: mipLevel,
                    color: args?.color ?? {r: 0, g: 0, b: 0, a: 0},
                })
            }
            // instantiate the shared page for all other pages
            for (let i = 1; i < pageDescriptors.length; i++) {
                this._pageManager.sharePage(pageDescriptors[i], pageHandleInfo)
            }
        }
    }

    copyToAtlas(targetRegion: Box2Like, mipLevel: number, sourceImage: HalImage, sourceOffset?: Vector2Like, sourceMipLevel?: number) {
        sourceOffset ??= {x: 0, y: 0}
        sourceMipLevel ??= 0
        const mipLevelSize = this.getMipLevelSize(mipLevel)
        // TODO for now explicitly clip the target region to the mip level size (maybe better to remove and let the caller handle it)
        targetRegion = {
            x: targetRegion.x,
            y: targetRegion.y,
            width: Math.min(targetRegion.width, mipLevelSize.width - targetRegion.x),
            height: Math.min(targetRegion.height, mipLevelSize.height - targetRegion.y),
        }
        if (
            targetRegion.x < 0 ||
            targetRegion.y < 0 ||
            targetRegion.x + targetRegion.width > mipLevelSize.width ||
            targetRegion.y + targetRegion.height > mipLevelSize.height
        ) {
            throw new Error("Target region is out of bounds.")
        }
        const allocatedUniquePageInfos = this.getOrAllocUniqueRegion(targetRegion, mipLevel)

        // TODO only do this when the write is not fully replacing the page
        // copy pages which came from shared pages
        const pagesToCopy = allocatedUniquePageInfos.filter((a) => a.sharedSourcePageHandleInfo !== undefined)
        if (pagesToCopy.length > 0) {
            // Group pages to copy by (srcAtlasId, dstAtlasId) pair
            type PairKey = string
            const pagesToCopyByAtlasPair = new Map<PairKey, typeof pagesToCopy>()
            for (const pageToCopy of pagesToCopy) {
                const srcAtlasId = pageToCopy.sharedSourcePageHandleInfo!.pageHandleByChannel[0].atlasId
                const dstAtlasId = pageToCopy.pageHandleInfo.pageHandleByChannel[0].atlasId
                const key: PairKey = `${srcAtlasId}_${dstAtlasId}`
                if (!pagesToCopyByAtlasPair.has(key)) pagesToCopyByAtlasPair.set(key, [])
                pagesToCopyByAtlasPair.get(key)!.push(pageToCopy)
            }

            for (const [key, pairPages] of pagesToCopyByAtlasPair) {
                const [srcAtlasId, dstAtlasId] = key.split("_").map(Number)
                const numPagesToCopySqrt = Math.ceil(Math.sqrt(pairPages.length * this.descriptor.numLayers))
                const tempBuffer = this.context.createPhysicalImage({
                    width: numPagesToCopySqrt * WebGl2ImageAtlas.pageSizeWithMargin,
                    height: numPagesToCopySqrt * WebGl2ImageAtlas.pageSizeWithMargin,
                    channelLayout: "R",
                    dataType: "float32",
                })
                const copyFromAtlasGeometry = this.context.createGeometry()
                const copyToAtlasGeometry = this.context.createGeometry()
                for (let i = 0; i < pairPages.length; i++) {
                    const pageToCopy = pairPages[i]
                    for (let c = 0; c < this.descriptor.numLayers; c++) {
                        const srcPageHandle = pageToCopy.sharedSourcePageHandleInfo!.pageHandleByChannel[c]
                        const dstPageHandle = pageToCopy.pageHandleInfo.pageHandleByChannel[c]
                        const linearIndex = i * this.descriptor.numLayers + c
                        const targetPosition = {
                            x: (linearIndex % numPagesToCopySqrt) * WebGl2ImageAtlas.pageSizeWithMargin,
                            y: Math.floor(linearIndex / numPagesToCopySqrt) * WebGl2ImageAtlas.pageSizeWithMargin,
                        }
                        copyFromAtlasGeometry.ref.addRect(
                            {
                                ...targetPosition,
                                width: WebGl2ImageAtlas.pageSizeWithMargin,
                                height: WebGl2ImageAtlas.pageSizeWithMargin,
                            },
                            {
                                x: srcPageHandle.pageIndex.x * WebGl2ImageAtlas.pageSizeWithMargin,
                                y: srcPageHandle.pageIndex.y * WebGl2ImageAtlas.pageSizeWithMargin,
                                width: WebGl2ImageAtlas.pageSizeWithMargin,
                                height: WebGl2ImageAtlas.pageSizeWithMargin,
                            },
                            {r: 0, g: 0, b: 0, a: 1},
                        )
                        copyToAtlasGeometry.ref.addRect(
                            {
                                x: dstPageHandle.pageIndex.x * WebGl2ImageAtlas.pageSizeWithMargin,
                                y: dstPageHandle.pageIndex.y * WebGl2ImageAtlas.pageSizeWithMargin,
                                width: WebGl2ImageAtlas.pageSizeWithMargin,
                                height: WebGl2ImageAtlas.pageSizeWithMargin,
                            },
                            {
                                ...targetPosition,
                                width: WebGl2ImageAtlas.pageSizeWithMargin,
                                height: WebGl2ImageAtlas.pageSizeWithMargin,
                            },
                            {r: 0, g: 0, b: 0, a: 1},
                        )
                    }
                }
                this.context.copyFromAtlas(tempBuffer.ref, copyFromAtlasGeometry.ref, this.context.getAtlasById(srcAtlasId))
                this.context.copyToAtlas(tempBuffer.ref, copyToAtlasGeometry.ref, this.context.getAtlasById(dstAtlasId))
                copyFromAtlasGeometry.release()
                copyToAtlasGeometry.release()
                tempBuffer.release()
            }
        }

        // write new pages — group by atlas to avoid writing to wrong atlas
        const writeGeometryRefs = new Map<number, ReturnType<typeof this.context.createGeometry>>()
        const pageHandleInfos = allocatedUniquePageInfos.map((pageInfo) => pageInfo.pageHandleInfo)
        for (const pageHandleInfo of pageHandleInfos) {
            const atlasId = pageHandleInfo.pageHandleByChannel[0].atlasId
            if (!writeGeometryRefs.has(atlasId)) {
                writeGeometryRefs.set(atlasId, this.context.createGeometry())
            }
            const geomRef = writeGeometryRefs.get(atlasId)!
            for (const pageDescriptor of pageHandleInfo.associatedPageDescriptorsByPageHash.values()) {
                if (pageDescriptor.mipLevel !== mipLevel) {
                    continue
                }
                if (pageHandleInfo.pageHandleByChannel.length !== this.descriptor.numLayers) {
                    throw new Error("Invalid number of channels.")
                }
                for (let channel = 0; channel < this.descriptor.numLayers; channel++) {
                    const pageHandle = pageHandleInfo.pageHandleByChannel[channel]
                    const regionIntersection = Box2.intersect(pageDescriptor.region, targetRegion)
                    if (!regionIntersection.isEmpty()) {
                        logger.trace(
                            `Copying to atlas: mipLevel=${pageDescriptor.mipLevel}, pageIndex=${traceObject(pageHandle.pageIndex)}, regionIntersection=${traceObject(regionIntersection)}, sourceOffset=${traceObject(sourceOffset)}, targetRegion=${traceObject(targetRegion)}`,
                        )
                        geomRef.ref.addRect(
                            {
                                x: pageHandle.pageIndex.x * WebGl2ImageAtlas.pageSizeWithMargin + regionIntersection.x - pageDescriptor.region.x,
                                y: pageHandle.pageIndex.y * WebGl2ImageAtlas.pageSizeWithMargin + regionIntersection.y - pageDescriptor.region.y,
                                width: regionIntersection.width,
                                height: regionIntersection.height,
                            },
                            {
                                x: sourceOffset.x + regionIntersection.x - targetRegion.x,
                                y: sourceOffset.y + regionIntersection.y - targetRegion.y,
                                width: regionIntersection.width,
                                height: regionIntersection.height,
                            },
                            {
                                r: channel,
                                g: sourceMipLevel,
                                b: 0,
                                a: 1,
                            },
                        )
                    }
                }
            }
        }
        for (const [atlasId, geomRef] of writeGeometryRefs) {
            this.context.copyToAtlas(sourceImage, geomRef.ref, this.context.getAtlasById(atlasId))
            geomRef.release()
        }
        this._maxRecentlyWrittenMipLevelPage = Math.max(this._maxRecentlyWrittenMipLevelPage, mipLevel)
    }

    // HalImage
    getMipLevelSize(mipLevel: number): Size2Like {
        return this.mipmapInfo.getMipLevelSize(mipLevel)
    }

    // HalImage
    readImageDataFloat(args?: ReadImageDataArgs): Float32Array {
        const mipLevel = args?.mipLevel ?? 0
        const mipLevelSize = this.getMipLevelSize(mipLevel)
        const region = args?.region ?? {x: 0, y: 0, ...mipLevelSize}
        if (mipLevel < 0 || mipLevel >= this.descriptor.numMipLevels) {
            throw new Error(`Invalid mip level: ${mipLevel}`)
        }
        const numChannels = getNumChannels(this.descriptor.channelLayout)
        const targetLineStride = region.width * numChannels
        const buffer = new Float32Array(region.width * region.height * numChannels)
        const pageDescriptors = this.determinePages(region, mipLevel)
        for (const pageDescriptor of pageDescriptors) {
            const pageHandleInfo = this._pageManager.getAllocatedPageHandleForPageDescriptor(pageDescriptor)
            if (!pageHandleInfo) {
                continue
            }
            const pageRegion = Box2.intersect(pageDescriptor.region, region)
            if (pageRegion.isEmpty()) {
                continue
            }
            const targetBaseX = pageRegion.x - region.x
            const targetBaseY = pageRegion.y - region.y
            for (let channel = 0; channel < this.descriptor.numLayers; channel++) {
                const pageHandle = pageHandleInfo.pageHandleByChannel[channel]
                const atlasPageRegion = {
                    x: pageHandle.pageIndex.x * WebGl2ImageAtlas.pageSize + pageRegion.x - pageDescriptor.region.x,
                    y: pageHandle.pageIndex.y * WebGl2ImageAtlas.pageSize + pageRegion.y - pageDescriptor.region.y,
                    width: Math.min(pageRegion.width, mipLevelSize.width),
                    height: Math.min(pageRegion.height, mipLevelSize.height),
                }
                const atlas = this.context.getAtlasById(pageHandle.atlasId)
                const pageData = atlas.image.readImageDataFloat({region: atlasPageRegion})
                let targetIndex = targetBaseY * targetLineStride + targetBaseX * numChannels + channel
                let sourceIndex = 0
                for (let sy = 0; sy < atlasPageRegion.height; sy++) {
                    for (let sx = 0; sx < atlasPageRegion.width; sx++) {
                        buffer[targetIndex] = pageData[sourceIndex]
                        targetIndex += numChannels
                        sourceIndex++
                    }
                    targetIndex += targetLineStride - atlasPageRegion.width * numChannels
                }
            }
        }
        return buffer
    }

    // HalImage
    async writeImageData({sourceData, sourceRegion, sourceMipLevel, targetOffset, targetLayer, targetMipLevel}: WriteImageDataArgs) {
        sourceRegion ??= {x: 0, y: 0, ...getSourceDataSize(sourceData)}
        sourceMipLevel ??= 0
        targetOffset ??= {x: 0, y: 0}
        targetLayer ??= 0
        targetMipLevel ??= 0

        if (targetLayer !== 0) {
            throw new Error("Multiple layers not supported for virtual image.")
        }

        // let's share all fully covered pages with the source image if it is also a virtual image
        if (USE_COPY_PAGE_SHARING_OPTIMIZATION && isHalImageVirtual(sourceData)) {
            if (!(sourceData instanceof WebGl2ImageVirtual)) {
                throw new Error("Invalid source image.")
            }
            if (targetOffset.x !== sourceRegion.x || targetOffset.y !== sourceRegion.y) {
                throw new Error("Virtual to virtual copy currently does not support shifting.")
            }
            if (targetMipLevel !== sourceMipLevel) {
                throw new Error("Virtual to virtual copy currently does not support mip level change.")
            }
            const pageSize = WebGl2ImageAtlas.pageSize
            const mipLevelSize = this.getMipLevelSize(targetMipLevel)
            if (
                sourceRegion.x % pageSize !== 0 ||
                sourceRegion.y % pageSize !== 0 ||
                (sourceRegion.width % pageSize !== 0 && targetOffset.x + sourceRegion.width < mipLevelSize.width) ||
                (sourceRegion.height % pageSize !== 0 && targetOffset.y + sourceRegion.height < mipLevelSize.height)
            ) {
                throw new Error("Virtual to virtual copy currently only supports page aligned copying.")
            }
            const targetPageDescriptors = this.determinePages(
                {
                    x: targetOffset.x,
                    y: targetOffset.y,
                    width: sourceRegion.width,
                    height: sourceRegion.height,
                },
                targetMipLevel,
            )
            targetPageDescriptors.forEach((targetPageDescriptor) => {
                const sourcePageDescriptor = sourceData._pageManager.getAllocatedPageHandleForPageDescriptor(targetPageDescriptor)
                if (!sourcePageDescriptor) {
                    throw new Error("Source page not found.")
                }
                this._pageManager.sharePage(targetPageDescriptor, sourcePageDescriptor)
            })
            return
        }

        let sourceImage: HalImage
        let sourceOffset: Vector2Like
        let ref: HalImageRef | undefined
        if (isHalImagePhysical(sourceData)) {
            sourceImage = sourceData
            sourceOffset = {x: sourceRegion.x, y: sourceRegion.y}
        } else {
            const channelLayout = getChannelLayoutFromImageSourceData(sourceData)
            const dataType = getDataTypeFromImageSourceData(sourceData)
            ref = this.context.createPhysicalImage({
                width: sourceRegion.width,
                height: sourceRegion.height,
                channelLayout: channelLayout,
                dataType: dataType,
            })
            sourceImage = ref.ref
            await sourceImage.writeImageData({
                sourceData,
                sourceRegion,
                sourceMipLevel,
            })
            sourceOffset = {x: 0, y: 0}
        }
        this.copyToAtlas(
            {
                x: targetOffset.x,
                y: targetOffset.y,
                width: sourceRegion.width,
                height: sourceRegion.height,
            },
            targetMipLevel,
            sourceImage,
            sourceOffset,
        )
        ref?.release()
    }

    prepareForUseAsSourceImage() {
        this._pageManager.flushPageTableUpdates()

        // Re-propagate coarser mip levels to missing pages whenever the page layout has changed
        // (pages written or evicted). _maxRecentlyWrittenMipLevelPage is set to numMipLevels-1
        // on every page eviction and to the current mip level on every write; it is reset to 0
        // after propagation completes. This ensures that stale propagated page-table entries
        // (written directly to the GPU texture by the propagation shader, bypassing the CPU diff
        // tracker) are re-evaluated before the image is sampled as a source. Without this, a
        // coarser-mip page evicted after propagation would leave finer-mip entries pointing at a
        // freed (and potentially reused) atlas slot, causing corrupted reads.
        // The _isPropagatingCoarserMipLevelsToMissingPages guard prevents infinite recursion
        // because propagateCoarserMipLevelsToMissingPages itself binds this image as a source.
        if (this._maxRecentlyWrittenMipLevelPage > 0 && !this._isPropagatingCoarserMipLevelsToMissingPages) {
            this.propagateCoarserMipLevelsToMissingPages()
        }
    }

    propagateCoarserMipLevelsToMissingPages() {
        this._isPropagatingCoarserMipLevelsToMissingPages = true
        this.context.propagateCoarserMipLevelsToMissingPages(this, this._maxRecentlyWrittenMipLevelPage)
        this._maxRecentlyWrittenMipLevelPage = 0
        this._isPropagatingCoarserMipLevelsToMissingPages = false
    }

    // HalImageVirtual
    determinePages(region: Box2Like, mipLevel: number): PageDescriptor[] {
        return this._pageManager.determinePages(region, mipLevel)
    }

    // HalImageVirtual
    // writePageImageData(pageDescriptor: WebGl2PageDescriptor, sourceData: HalImageSourceData, sourceRegion?: Box2Like) {
    //     this.writeImageData({
    //         sourceData,
    //         sourceRegion,
    //         targetOffset: pageDescriptor.region,
    //         targetMipLevel: pageDescriptor.mipLevel,
    //     })
    // }

    // HalImageVirtual
    freeAllPages(): void {
        this._pageManager.freePages(
            this._pageManager.getAllocatedPageHandles().map((pageHandleInfo) => pageHandleInfo.linearPageIndex),
            true,
        )
    }

    // HalImageVirtual
    freePages(pageDescriptors: readonly PageDescriptor[]): void {
        const pageHandleInfos = pageDescriptors
            .map((pageDescriptor) => this._pageManager.getAllocatedPageHandleForPageDescriptor(pageDescriptor))
            .filter(IsDefined)
        const uniquePageHandleInfos = Array.from(new Set(pageHandleInfos))
        this._pageManager.freePages(
            uniquePageHandleInfos.map((pageHandleInfo) => pageHandleInfo.linearPageIndex),
            true,
        )
    }

    private getOrAllocUniqueRegion(region: Box2Like, mipLevel: number): AllocatedUniquePageInfo[] {
        if (mipLevel < 0 || mipLevel >= this.descriptor.numMipLevels) {
            throw new Error(`Invalid mip level: ${mipLevel}`)
        }
        logger.trace("getOrAllocRegion", region, mipLevel)
        // determine which pages are still missing
        const allocatedUniquePageInfos: AllocatedUniquePageInfo[] = []
        const pageDescriptors = this.determinePages(region, mipLevel)
        for (const pageDescriptor of pageDescriptors) {
            const allocatedUniquePageInfo = this._pageManager.getOrAllocUniquePage(pageDescriptor)
            allocatedUniquePageInfos.push(allocatedUniquePageInfo)
        }
        return allocatedUniquePageInfos
    }

    // HalImageVirtual
    // registerPageUsage(pageDescriptors: readonly PageDescriptor[]): void {
    //     pageDescriptors.forEach((pageDescriptor) => {
    //         const webGl2PageDescriptor = this._pageManager.pageDescriptorsByPageHash.get(pageDescriptor.hash)
    //         if (webGl2PageDescriptor) {
    //             webGl2PageDescriptor.pageHandleByChannel.forEach((pageHandle) => this._pageManager.atlas.registerPageUsage(pageHandle))
    //         }
    //     })
    // }

    // HalImageVirtual
    lockPages(pageDescriptors: readonly PageDescriptor[]): void {
        this._pageManager.lockPages(pageDescriptors)
    }

    // HalImageVirtual
    unlockPages(pageDescriptors: readonly PageDescriptor[]): void {
        this._pageManager.unlockPages(pageDescriptors)
    }

    // HalImageVirtual
    isLocked(pageDescriptor: PageDescriptor): boolean {
        return this._pageManager.getLockCount(pageDescriptor) > 0
    }

    // private readPageIndices(): Uint8Array {
    //     if (!this._pageManager) {
    //         throw Error("Atlas descriptor is not set.")
    //     }
    //     const numChannels = 4
    //     const numElements = this._pageManager.numPages.x * this._pageManager.numPages.y * numChannels
    //     const rawImageData = new Uint8Array(numElements)
    //     const gl = this.context.gl
    //     gl.bindTexture(gl.TEXTURE_2D_ARRAY, this._descriptor.texture)
    //     const readFbo = gl.createFramebuffer()
    //     gl.bindFramebuffer(gl.READ_FRAMEBUFFER, readFbo)
    //     gl.pixelStorei(gl.PACK_ALIGNMENT, 1) // make sure to tightly pack the data
    //     gl.framebufferTextureLayer(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, this._descriptor.texture, 0, 0)
    //     gl.readPixels(0, 0, this._pageManager.numPages.x, this._pageManager.numPages.y, gl.RGBA_INTEGER, gl.UNSIGNED_BYTE, rawImageData)
    //     checkForGlError(gl, "Reading pixels")
    //     gl.deleteFramebuffer(readFbo)
    //     return rawImageData
    // }

    // TODO for debugging purposes only
    get pageManager(): WebGl2ImageVirtualPageManager {
        return this._pageManager
    }

    private _pageManager: WebGl2ImageVirtualPageManager
    private _currentDrawInfo: DrawInfo | null = null
    private _maxRecentlyWrittenMipLevelPage = 0
    private _isPropagatingCoarserMipLevelsToMissingPages = false
}

const DEFAULT_PAGE_PRIORITY = 1

type DrawInfo = {
    pageExtendedClipRegion: Box2Like
    bufferImage: HalImageRef
    bufferDrawArgs: DrawArgs
}

const logger = loggerFactory.getLogger("webgl2")
